import React, { useCallback, useState, FC } from 'react';

import api from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { BspData } from '../types';
import Context, { MatchWithVoucherBody } from './context';

interface Props {
    children: React.ReactNode;
}

const BspDataProvider: FC<Props> = (props) => {
    const { children } = props;
    const [data, setData] = useState<Array<BspData>>([]);
    const [selected, setSelected] = useState<BspData | null>(null);
    const [error, setError] = useState({});

    const handleGetData = useCallback((res) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(api, data, handleGetData, setError);

    const getOne = useGetOne(api, setSelected, setError);

    const handleSetData = useCallback((item, newData) => {
        setData(newData);
    }, []);
    const post = usePost(api, data, handleSetData, setError);

    const put = usePut(api, data, handleSetData, setError);

    const handleDeleteCallback = useCallback((id, newData) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(api, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(api, data, handleDeleteCallback, setError);

    const uploadDataFromFile = useCallback(async (body: Array<Partial<BspData>>) => {
        try {
            const newData: BspData[] = await api.uploadDataFromFile(body);
            return newData;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const matchWithVoucher = useCallback(async (body: MatchWithVoucherBody) => {
        try {
            const newData: BspData[] = await api.matchWithVoucher(body);
            return newData;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const query = useCallback(async (query) => {
        try {
            const foundData = await api.query(query);
            setData(foundData);
            return foundData;
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    const updateDocumentApproval = useCallback(async (documentId: string, isApproved: boolean) => {
        try {
            return await api.updateDocumentApproval(documentId, isApproved);
        } catch (err: any) {
            setError(err);
            throw err;
        }
    }, []);

    return (
        <Context.Provider
            value={{
                get,
                getOne,
                post,
                put,
                remove,
                softDelete,
                data,
                setData,
                selected,
                setSelected,
                error,
                uploadDataFromFile,
                matchWithVoucher,
                query,
                updateDocumentApproval
            }}
        >
            {children}
        </Context.Provider>
    );
};

export default BspDataProvider;
